import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../AuthContext';

interface Step {
  key: string;
  label: string;
  description: string;
  done: boolean;
  count: number;
  link: string;
}
interface Checklist { steps: Step[]; completed: number; total: number; }

const DISMISS_KEY = 'cg_onboarding_dismissed';

export function OnboardingChecklist() {
  const [data, setData] = useState<Checklist | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    if (dismissed) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/onboarding`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } catch { /* silent — checklist is non-critical */ }
    })();
  }, [dismissed]);

  if (dismissed || !data || data.completed === data.total) return null;

  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <div className="mt-8 rounded-xl border border-primary/30 bg-card p-6" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Tenant onboarding checklist</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {data.completed} of {data.total} steps complete — finish setup to get the most out of CloudGenie.
          </p>
        </div>
        <button
          data-testid="onboarding-dismiss"
          onClick={() => { localStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); }}
          className="text-xs text-muted-foreground hover:text-foreground shrink-0"
        >
          Dismiss
        </button>
      </div>

      <div className="mt-4 h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="onboarding-progress" />
      </div>

      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {data.steps.map((s) => (
          <li key={s.key} data-testid={`onboarding-step-${s.key}`}
              className={`flex items-start gap-3 rounded-lg border p-4 ${s.done ? 'border-emerald-700/40 bg-emerald-900/10' : 'border-border bg-background/40'}`}>
            <span aria-hidden
                  className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${s.done ? 'bg-emerald-600 text-white' : 'border border-border text-muted-foreground'}`}>
              {s.done ? '✓' : ''}
            </span>
            <div className="min-w-0">
              <p className={`text-sm font-medium ${s.done ? 'line-through text-muted-foreground' : ''}`}>{s.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
              {!s.done && (
                <Link to={s.link} data-testid={`onboarding-cta-${s.key}`}
                      className="mt-2 inline-block text-xs text-primary hover:underline">
                  Set up →
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
